// Iter 142 — Schedule page: full daily prediction catalogue grouped by league.
// Day-strip (Yesterday → +7d) + "Go to date" calendar popover.
// Each league is a collapsible section with all fixtures + every market
// prediction we computed.  Per-league "best-hit market" badge driven by
// settled prediction_results over the last 90 days.
//
// Click tracking (`POST /api/schedule/click`) drives ingestion-priority
// decisions: leagues with high engagement get more frequent recompute.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  ChevronDown, ChevronUp, ChevronRight, CalendarDays, TrendingUp, Loader2, Tv,
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { countryToFlag } from "../lib/countryFlag";
import ConfidenceTrendSparkline from "../components/ConfidenceTrendSparkline";
import QuantumCompareBadge, { useQuantumComparison } from "../components/QuantumCompareBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Top20PicksList, X5OddsBuilder, EasyWinsList, Top20EliteList, Top20MasterList, X5EliteBuilder } from "../components/ScheduleSubViews";
import { formatLiveMinute } from "../lib/liveMinute";
import { appTodayIso, dayStripDates, fmtKickoffTime } from "../lib/appTime";

const fmtIsoDate = (dt) => {
  // Calendar-picker dates are local-midnight Dates; read the local components
  // (NOT toISOString) so the string matches the day the user clicked.
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const RISK_COLORS = {
  Low:    "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  Medium: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  High:   "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

const fmtKickoff = (iso) => fmtKickoffTime(iso);

const SchedulePage = ({ embedded = false, onFixtureClick = null, engine = "standard" } = {}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = searchParams.get("d") || appTodayIso();

  const [date, setDate] = useState(initialDate);
  // Iter 231 — sub-view tab.  URL-sticky so deep links open the right view.
  const [subTab, setSubTab] = useState(searchParams.get("v") || "leagues");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [expanded, setExpanded] = useState(new Set());
  const [accuracyByLeague, setAccuracyByLeague] = useState({}); // league_id -> best_market
  const [calOpen, setCalOpen] = useState(false);

  const strip = useMemo(() => dayStripDates(), []);

  // Sync the URL ?d= param.
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    let changed = false;
    if (sp.get("d") !== date) { sp.set("d", date); changed = true; }
    if (sp.get("v") !== subTab) {
      if (subTab && subTab !== "leagues") sp.set("v", subTab);
      else sp.delete("v");
      changed = true;
    }
    if (changed) setSearchParams(sp, { replace: true });
    // eslint-disable-next-line
  }, [date, subTab]);

  // Load the day's schedule.
  // Iter 145aw — explicit 60s timeout (mobile browsers cut off at ~30s
  // by default) + retry button surface so cold loads of busy days
  // (~12-20s after indexing, can spike higher on first hit) don't
  // dead-end the user with a generic "Couldn't load" toast.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    setLoadError(null);
    axios.get(`${API}/schedule/day?date=${date}&engine=${engine}`, { timeout: 120000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.code === "ECONNABORTED"
          ? "Schedule load timed out — tap retry."
          : "Couldn't load schedule";
        setLoadError(msg);
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, loadAttempt, engine]);

  // Iter 145g — Auto-refresh every 60s when there are live fixtures so
  // scores + minute + outcomes ticker without a manual reload.  No-op
  // if the day has no live games (saves API calls on archive views).
  //
  // Iter 145l — Upgraded to SSE so live ticks arrive in ≈15s with
  // bandwidth-friendly delta payloads instead of the full 100KB+
  // /schedule/day blob.  Falls back to a 60s polling refresh if the
  // EventSource API is unavailable or the connection drops.
  const liveFixtureCount = useMemo(() => {
    if (!data) return 0;
    let n = 0;
    for (const lg of data.leagues || []) {
      for (const f of lg.fixtures || []) {
        if (f.live && f.live.is_live) n++;
      }
    }
    return n;
  }, [data]);

  // Helper — merge an SSE delta into the existing schedule payload.
  const applyLiveDelta = (delta) => {
    setData((cur) => {
      if (!cur || !delta || !Array.isArray(delta.fixtures)) return cur;
      const byFid = new Map(delta.fixtures.map((f) => [f.fixture_id, f]));
      const next = { ...cur, leagues: cur.leagues.map((lg) => ({
        ...lg,
        fixtures: lg.fixtures.map((f) => {
          const u = byFid.get(f.fixture_id);
          if (!u) return f;
          // Merge live block + hit + per-market outcomes.
          const updated = {
            ...f,
            live: {
              ...(f.live || {}),
              is_live: u.is_live,
              is_finished: u.is_finished,
              status_short: u.status,
              minute: u.minute,
              home_score: u.home_score,
              away_score: u.away_score,
            },
            hit: u.hit || f.hit,
          };
          if (Array.isArray(u.outcomes) && u.outcomes.length) {
            const byKey = new Map(u.outcomes.map((o) => [`${o.market_type}|${o.prediction}`, o.outcome]));
            updated.markets = f.markets.map((m) => {
              const k = `${m.market_type}|${m.prediction}`;
              return byKey.has(k) ? { ...m, outcome: byKey.get(k) } : m;
            });
          }
          return updated;
        }),
      })) };
      return next;
    });
  };

  useEffect(() => {
    if (liveFixtureCount === 0) return undefined;
    // Prefer SSE; fall back to polling on any error.
    let es = null;
    let pollT = null;
    let cancelled = false;
    try {
      es = new EventSource(`${API}/schedule/live-stream?date=${date}&interval=15`);
      es.addEventListener("tick", (e) => {
        if (cancelled) return;
        try { applyLiveDelta(JSON.parse(e.data)); } catch {}
      });
      es.onerror = () => {
        if (cancelled) return;
        try { es && es.close(); } catch {}
        es = null;
        // Polling fallback @ 60s.
        pollT = setInterval(() => {
          axios.get(`${API}/schedule/day?date=${date}&engine=${engine}`).then((r) => setData(r.data)).catch(() => {});
        }, 60000);
      };
    } catch {
      pollT = setInterval(() => {
        axios.get(`${API}/schedule/day?date=${date}&engine=${engine}`).then((r) => setData(r.data)).catch(() => {});
      }, 60000);
    }
    return () => {
      cancelled = true;
      if (es) try { es.close(); } catch { /* ignore */ }
      if (pollT) clearInterval(pollT);
    };
  }, [liveFixtureCount, date, engine]);

  // Lazily load league-accuracy stats for the visible leagues — only
  // for leagues currently expanded, batched in parallel.  Skips leagues
  // already loaded.
  useEffect(() => {
    const toLoad = (data?.leagues || [])
      .filter((lg) => expanded.has(lg.league_id) && lg.league_id && !accuracyByLeague[lg.league_id])
      .slice(0, 8);
    if (!toLoad.length) return;
    let cancelled = false;
    Promise.all(
      toLoad.map((lg) =>
        axios
          .get(`${API}/schedule/league-accuracy?league_id=${lg.league_id}&days=90`)
          .then((r) => [lg.league_id, r.data])
          .catch(() => [lg.league_id, null])
      )
    ).then((pairs) => {
      if (cancelled) return;
      setAccuracyByLeague((prev) => {
        const next = { ...prev };
        pairs.forEach(([lid, val]) => { if (lid) next[lid] = val; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [expanded, data, accuracyByLeague]);

  const toggleLeague = (lid) => {
    if (!lid) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(lid) ? next.delete(lid) : next.add(lid);
      return next;
    });
  };

  const onPickFixture = (fixture, league_id) => {
    // Click tracking — fire-and-forget, anonymous OK.
    axios.post(`${API}/schedule/click`, {
      match_id: fixture.match_id,
      league_id,
    }).catch(() => {});
    if (typeof onFixtureClick === "function") {
      onFixtureClick(fixture, league_id);
      return;
    }
    navigate(`/match/${fixture.match_id}`);
  };

  return (
    <div className={embedded ? "text-foreground" : "min-h-screen bg-background text-foreground"}>
      {!embedded && <TopNav />}

      <div className={embedded ? "" : "max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14 pb-24"}>
        <header className="mb-10" data-testid="schedule-header">
          <div
            className="text-[11px] uppercase inline-flex items-center gap-2 mb-3"
            style={{ color: "var(--v3-text-3)", letterSpacing: "0.14em", fontFamily: "var(--v3-font-mono)" }}
          >
            <span
              className="h-1 w-1 rounded-full"
              style={{ background: "var(--v3-indigo)" }}
            />
            Schedule · {date}
          </div>
          <h1
            className="text-4xl md:text-5xl"
            data-testid="schedule-page-title"
            style={{ fontWeight: 500, letterSpacing: "-0.045em", lineHeight: 1.05 }}
          >
            {embedded ? "GOLD Schedule" : "Every match-day"}<br />
            <span style={{ color: "var(--v3-text-3)" }}>
              {embedded ? "" : "prediction we have."}
            </span>
          </h1>
          <p
            className="mt-5 max-w-xl text-[15px] leading-relaxed"
            style={{ color: "var(--v3-text-2)", letterSpacing: "-0.005em" }}
          >
            {embedded
              ? "Every fixture across the next 8 days, grouped by league. Click any match to open the 20-layer GOLD Dossier."
              : "Grouped by league. Pick any day in the strip below or jump to a date."}
          </p>
        </header>

        {/* Day strip — v3 flat mono pills, hairline divider underneath */}
        <div
          className="flex items-stretch gap-1 mb-6 overflow-x-auto pb-3 -mx-4 px-4"
          style={{ borderBottom: "1px solid var(--v3-line)" }}
          data-testid="schedule-daystrip"
        >
          {strip.map((d) => {
            const active = date === d.iso;
            return (
              <button
                key={d.iso}
                type="button"
                data-testid={`day-strip-${d.iso}`}
                onClick={() => setDate(d.iso)}
                className="relative flex flex-col items-center justify-center px-4 py-2.5 min-w-[72px] shrink-0 transition"
                style={{
                  background: "transparent",
                  color: active ? "var(--v3-text)" : "var(--v3-text-3)",
                }}
              >
                <span
                  className="text-[10px] uppercase"
                  style={{
                    letterSpacing: "0.12em",
                    fontFamily: "var(--v3-font-mono)",
                    fontWeight: 500,
                  }}
                >
                  {d.label}
                </span>
                <span
                  className="text-[13px] mt-1"
                  style={{
                    fontFamily: "var(--v3-font-mono)",
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {d.sub}
                </span>
                {active && (
                  <span
                    className="absolute -bottom-[13px] left-3 right-3 h-px"
                    style={{ background: "var(--v3-text)" }}
                  />
                )}
              </button>
            );
          })}
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="schedule-goto-date-btn"
                className="flex flex-col items-center justify-center px-4 py-2.5 min-w-[72px] shrink-0 transition"
                style={{ color: "var(--v3-text-3)" }}
              >
                <CalendarDays className="w-4 h-4" strokeWidth={1.75} />
                <span
                  className="text-[10px] uppercase mt-1"
                  style={{ letterSpacing: "0.12em", fontFamily: "var(--v3-font-mono)" }}
                >
                  Go to
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" style={{ background: "var(--v3-surface)", border: "1px solid var(--v3-line)" }} align="end">
              <Calendar
                mode="single"
                selected={new Date(date + "T00:00:00")}
                onSelect={(d) => {
                  if (!d) return;
                  setDate(fmtIsoDate(d));
                  setCalOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Summary — v3 subtle, mono numerics */}
        {!loading && data && (
          <div
            className="mb-8 flex items-center gap-x-5 gap-y-1 text-[13px] flex-wrap"
            data-testid="schedule-summary"
            style={{ color: "var(--v3-text-3)" }}
          >
            <span>
              <span
                style={{
                  color: "var(--v3-text)",
                  fontFamily: "var(--v3-font-mono)",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 500,
                }}
              >
                {data.total_fixtures}
              </span>{" "}
              fixtures
            </span>
            <span>
              <span
                style={{
                  color: "var(--v3-text)",
                  fontFamily: "var(--v3-font-mono)",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 500,
                }}
              >
                {data.total_leagues}
              </span>{" "}
              leagues
            </span>
            <span>
              <span
                style={{
                  color: "var(--v3-indigo-fg)",
                  fontFamily: "var(--v3-font-mono)",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 500,
                }}
              >
                {data.leagues.reduce((s, lg) => s + lg.fixtures.filter(f => f.has_prediction).length, 0)}
              </span>{" "}
              with predictions
            </span>
            {liveFixtureCount > 0 && (
              <span
                className="inline-flex items-center gap-1.5"
                data-testid="schedule-summary-live"
                title="Auto-refreshes every 60s"
                style={{ color: "#f43f5e" }}
              >
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#f43f5e" }} />
                <span style={{ fontFamily: "var(--v3-font-mono)", fontWeight: 500 }}>{liveFixtureCount} live</span>
              </span>
            )}
            {(() => {
              let settled = 0, won = 0;
              for (const lg of data.leagues || []) {
                for (const f of lg.fixtures || []) {
                  if (f.hit) { settled += f.hit.settled; won += f.hit.won; }
                }
              }
              if (settled === 0) return null;
              const hr = Math.round(won * 1000 / settled) / 10;
              const tone = hr >= 65 ? "#22c55e" : hr >= 50 ? "#eab308" : "#f43f5e";
              return (
                <span data-testid="schedule-summary-hit">
                  <span
                    style={{
                      color: tone,
                      fontFamily: "var(--v3-font-mono)",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 500,
                    }}
                  >
                    {won}/{settled}
                  </span>{" "}
                  picks · {hr}%
                </span>
              );
            })()}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8" data-testid="schedule-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading schedule…
          </div>
        )}

        {/* Error with retry — Iter 145aw */}
        {!loading && loadError && !data && (
          <Card className="border-rose-500/30 bg-rose-500/5" data-testid="schedule-load-error">
            <CardContent className="p-6 text-center space-y-3">
              <p className="text-base font-semibold text-rose-300">{loadError}</p>
              <p className="text-sm text-muted-foreground">
                The slate for {date} is taking longer than expected.  Tap retry to try again.
              </p>
              <Button
                size="sm"
                onClick={() => setLoadAttempt((n) => n + 1)}
                data-testid="schedule-retry-btn"
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty */}
        {!loading && data && data.total_fixtures === 0 && (
          <Card className="border-white/10" data-testid="schedule-empty">
            <CardContent className="p-8 text-center">
              <p className="text-base font-semibold mb-1">No predictions for this date</p>
              <p className="text-sm text-muted-foreground">
                We only show fixtures whose real-form data is available. Try a date closer to today.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Iter 231 — Sub-views: All Leagues / Top 20 Picks / x5 Odds — v3 hairline tab rail */}
        {!loading && data && data.total_fixtures > 0 && (
          <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
            <TabsList
              className="mb-8 bg-transparent border-0 h-auto p-0 flex flex-wrap gap-x-6 gap-y-2 w-full justify-start rounded-none"
              style={{ borderBottom: "1px solid var(--v3-line)" }}
              data-testid="schedule-subtabs"
            >
              <TabsTrigger
                value="leagues"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] data-[state=active]:text-[color:var(--v3-text)] text-[color:var(--v3-text-3)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="schedule-subtab-leagues"
              >
                All Leagues
              </TabsTrigger>
              <TabsTrigger
                value="top20"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] data-[state=active]:text-[color:var(--v3-text)] text-[color:var(--v3-text-3)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="schedule-subtab-top20"
              >
                Top 20 Picks
              </TabsTrigger>
              <TabsTrigger
                value="top20elite"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] data-[state=active]:text-[color:var(--v3-text)] text-[color:var(--v3-text-3)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="schedule-subtab-top20elite"
              >
                Top 20 Elite
                <span
                  className="ml-1.5 text-[9px] uppercase px-1 py-px rounded"
                  style={{
                    background: "rgba(99,102,241,0.1)",
                    color: "var(--v3-indigo-fg)",
                    border: "1px solid rgba(99,102,241,0.3)",
                    letterSpacing: "0.08em",
                    fontFamily: "var(--v3-font-mono)",
                  }}
                >
                  new
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="top20master"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] data-[state=active]:text-[color:var(--v3-text)] text-[color:var(--v3-text-3)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="schedule-subtab-top20master"
              >
                Top 20 Master
                <span
                  className="ml-1.5 text-[9px] uppercase px-1 py-px rounded"
                  style={{
                    color: "var(--v3-text-3)",
                    border: "1px solid var(--v3-line-2)",
                    letterSpacing: "0.08em",
                    fontFamily: "var(--v3-font-mono)",
                  }}
                >
                  week
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="x5"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] data-[state=active]:text-[color:var(--v3-text)] text-[color:var(--v3-text-3)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="schedule-subtab-x5"
              >
                x5 Odds
              </TabsTrigger>
              <TabsTrigger
                value="x5elite"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] data-[state=active]:text-[color:var(--v3-text)] text-[color:var(--v3-text-3)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="schedule-subtab-x5elite"
              >
                x5 Elite
                <span
                  className="ml-1.5 text-[9px] uppercase px-1 py-px rounded"
                  style={{
                    background: "rgba(99,102,241,0.1)",
                    color: "var(--v3-indigo-fg)",
                    border: "1px solid rgba(99,102,241,0.3)",
                    letterSpacing: "0.08em",
                    fontFamily: "var(--v3-font-mono)",
                  }}
                >
                  new
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="easywin"
                className="relative px-0 pb-3 pt-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[13px] data-[state=active]:text-[color:var(--v3-text)] text-[color:var(--v3-text-3)] after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-transparent data-[state=active]:after:bg-[color:var(--v3-text)]"
                data-testid="schedule-subtab-easywin"
              >
                Easy Wins
              </TabsTrigger>
            </TabsList>

            <TabsContent value="leagues" className="mt-0 space-y-0">
              {data.leagues.map((lg) => {
                const isOpen = expanded.has(lg.league_id);
                const acc = accuracyByLeague[lg.league_id];
                return (
                  <LeagueSection
                    key={`${lg.league_id}-${lg.league_name}`}
                    league={lg}
                    open={isOpen}
                    onToggle={() => toggleLeague(lg.league_id)}
                    accuracy={acc}
                    onPickFixture={onPickFixture}
                  />
                );
              })}
            </TabsContent>

            <TabsContent value="top20" className="mt-0">
              <Top20PicksList data={data} date={date} onPickFixture={onPickFixture} />
            </TabsContent>

            <TabsContent value="top20elite" className="mt-0">
              <Top20EliteList date={date} onPickFixture={onPickFixture} />
            </TabsContent>

            <TabsContent value="top20master" className="mt-0">
              <Top20MasterList date={date} onPickFixture={onPickFixture} />
            </TabsContent>

            <TabsContent value="x5" className="mt-0">
              <X5OddsBuilder data={data} date={date} onPickFixture={onPickFixture} />
            </TabsContent>

            <TabsContent value="x5elite" className="mt-0">
              <X5EliteBuilder date={date} onPickFixture={onPickFixture} />
            </TabsContent>

            <TabsContent value="easywin" className="mt-0">
              <EasyWinsList data={data} date={date} onPickFixture={onPickFixture} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

const LeagueSection = ({ league, open, onToggle, accuracy, onPickFixture }) => {
  const flag = countryToFlag(league.league_country) || "🌍";
  const bestMarket = accuracy?.best_market;
  return (
    <div
      className="relative"
      data-testid={`schedule-league-${league.league_id}`}
      style={{ borderBottom: "1px solid var(--v3-line)" }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`schedule-league-toggle-${league.league_id}`}
        className="group w-full flex items-center gap-4 py-4 transition text-left"
      >
        <span className="text-base leading-none">{flag}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span
              className="truncate text-[15px]"
              style={{ fontWeight: 500, letterSpacing: "-0.015em" }}
            >
              {league.league_name}
            </span>
            <span
              className="text-[10px] uppercase"
              style={{
                color: "var(--v3-text-3)",
                letterSpacing: "0.12em",
                fontFamily: "var(--v3-font-mono)",
              }}
            >
              {league.league_country}
            </span>
          </div>
          <div
            className="flex items-center gap-3 mt-1 text-[12px] flex-wrap"
            style={{ color: "var(--v3-text-3)" }}
          >
            <span>
              <span
                style={{
                  color: "var(--v3-text-2)",
                  fontFamily: "var(--v3-font-mono)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {league.fixtures.length}
              </span>{" "}
              fixtures
            </span>
            {bestMarket && (
              <span
                className="inline-flex items-center gap-1.5"
                data-testid={`schedule-best-market-${league.league_id}`}
                title="Best-hit market in this league over last 90 days"
                style={{ color: "var(--v3-indigo-fg)" }}
              >
                <TrendingUp className="w-3 h-3" strokeWidth={1.75} />
                <span style={{ fontWeight: 500 }}>{bestMarket.market_type}</span>
                <span style={{ color: "var(--v3-text-3)" }}>
                  · {bestMarket.hit_rate}% ({bestMarket.settled})
                </span>
              </span>
            )}
          </div>
        </div>
        <span
          className="transition-transform"
          style={{
            color: "var(--v3-text-3)",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
          }}
        >
          <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
        </span>
      </button>

      {open && (
        <div className="pb-5 space-y-3">
          {league.league_id && (
            <div
              className="rounded-md px-3 py-2 text-[12px]"
              data-testid={`schedule-league-trend-${league.league_id}`}
              style={{
                background: "var(--v3-surface)",
                border: "1px solid var(--v3-line)",
                color: "var(--v3-text-2)",
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold mb-1.5">
                30-day confidence trend
              </div>
              <ConfidenceTrendSparkline leagueId={league.league_id} days={30} />
            </div>
          )}
          {league.fixtures.map((f) => (
            <FixtureRow
              key={f.match_id}
              fixture={f}
              onClick={() => onPickFixture(f, league.league_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FixtureRow = ({ fixture, onClick }) => {
  const [marketsOpen, setMarketsOpen] = useState(false);
  const hasPrediction = fixture.has_prediction && fixture.markets.length > 0;
  // Iter 145bh — Lazily fetch Quantum picks once the user expands "Show markets".
  // Use the integer fixture_id (NOT match_id, which is a UUID string).
  const quantumItems = marketsOpen
    ? (fixture.markets || []).map((m) => ({
        fixture_id: fixture.fixture_id,
        market_type: m.market_type,
      }))
    : [];
  const quantumLookup = useQuantumComparison(quantumItems);
  const live = fixture.live;
  const hit = fixture.hit;
  const isLive = !!(live && live.is_live);
  const isFinished = !!(live && live.is_finished);
  const scoreLine =
    live && live.home_score != null && live.away_score != null
      ? `${live.home_score}-${live.away_score}`
      : null;

  // Hit rate badge tone (only shown once at least one market settled).
  const hitTone = !hit
    ? ""
    : hit.hit_rate >= 75
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
    : hit.hit_rate >= 50
    ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
    : "bg-rose-500/15 text-rose-300 border-rose-500/30";

  return (
    <div
      data-testid={`schedule-fixture-${fixture.match_id}`}
      className={[
        "rounded-lg border overflow-hidden transition",
        isLive
          ? "border-rose-500/40 bg-rose-500/[0.04]"
          : "border-white/10 bg-white/[0.02]",
      ].join(" ")}
    >
      <div className="flex items-center gap-3 p-3">
        <button
          type="button"
          onClick={onClick}
          className="flex-1 text-left min-w-0 hover:opacity-80 transition"
          data-testid={`schedule-fixture-open-${fixture.match_id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            {isLive ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-rose-200 bg-rose-500/20 border border-rose-500/40 rounded-full px-1.5 py-0.5"
                data-testid={`schedule-fixture-live-${fixture.match_id}`}
              >
                <span className="w-1.5 h-1.5 bg-rose-400 rounded-full animate-pulse" />
                LIVE
                {live.status_short === "HT"
                  ? " · HT"
                  : live.minute != null
                  ? ` · ${formatLiveMinute(live.minute, live.minute_extra, live.status) || `${live.minute}'`}`
                  : live.status_short
                  ? ` · ${live.status_short}`
                  : ""}
              </span>
            ) : (
              <span className="text-[11px] font-mono opacity-70">{fmtKickoff(fixture.kickoff_iso)}</span>
            )}
            <span className="font-semibold truncate">{fixture.home_team?.name}</span>
            {scoreLine ? (
              <span
                className="font-bold font-tabular tabular-nums text-foreground bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-xs"
                data-testid={`schedule-fixture-score-${fixture.match_id}`}
              >
                {scoreLine}
              </span>
            ) : (
              <span className="text-muted-foreground text-xs">vs</span>
            )}
            {/* Iter 145w — Halftime chip: surfaces "(HT 0-1)" alongside the
                running score so HT-market settlement context is obvious
                without opening the live page. Shows after HT has been
                played (live.status_short is HT/2H/ET/FT) and HT data
                is available. */}
            {(live?.ht_home != null && live?.ht_away != null && ["HT", "2H", "ET", "BT", "P", "FT", "AET", "PEN"].includes(live?.status_short)) && (
              <span
                className="text-[10px] font-tabular tabular-nums font-semibold text-muted-foreground/90 bg-foreground/5 border border-border/60 rounded px-1.5 py-0.5 whitespace-nowrap"
                data-testid={`schedule-fixture-ht-${fixture.match_id}`}
                title="Halftime score"
              >
                HT {live.ht_home}-{live.ht_away}
              </span>
            )}
            <span className="font-semibold truncate">{fixture.away_team?.name}</span>
            {isFinished && (
              <span className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-bold ml-1">
                {live.status_short || "FT"}
              </span>
            )}
            {hit && (
              <span
                className={[
                  "inline-flex items-center text-[10px] font-bold tracking-wide rounded-full px-1.5 py-0.5 border font-tabular",
                  hitTone,
                ].join(" ")}
                data-testid={`schedule-fixture-hit-${fixture.match_id}`}
                title={`${hit.won}/${hit.settled} markets correct`}
              >
                {hit.won}/{hit.settled} · {hit.hit_rate}%
              </span>
            )}
          </div>
          <div className="text-[11px] mt-0.5">
            {hasPrediction ? (
              <span className="text-muted-foreground">
                {fixture.markets.length} market prediction{fixture.markets.length === 1 ? "" : "s"}
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 text-amber-300/90"
                data-testid={`schedule-fixture-pending-${fixture.match_id}`}
                title="Real form data still being gathered for this fixture"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                Predictions pending — gathering form data
              </span>
            )}
          </div>
        </button>
        {hasPrediction && (
          <button
            type="button"
            onClick={() => setMarketsOpen((v) => !v)}
            data-testid={`schedule-fixture-markets-toggle-${fixture.match_id}`}
            className="text-xs font-bold tracking-wide text-cyan-300 hover:text-cyan-200 px-2 py-1 rounded"
          >
            {marketsOpen ? "Hide markets" : "Show markets"}
          </button>
        )}
      </div>
      {hasPrediction && marketsOpen && (
        <div className="border-t border-white/10 p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {fixture.markets.map((m, i) => {
            // Iter 145g — outcome stripe on the right edge of each card.
            const oc = m.outcome;
            const outcomeTone =
              oc === true
                ? "border-emerald-500/40 bg-emerald-500/[0.06]"
                : oc === false
                ? "border-rose-500/40 bg-rose-500/[0.06]"
                : "border-white/10 bg-white/[0.03]";
            return (
              <div
                key={i}
                data-testid={`schedule-market-${fixture.match_id}-${i}`}
                className={`rounded border p-2 ${outcomeTone}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold flex items-center gap-1.5">
                    {m.market_type}
                    {/* Iter 85c — Engine badge.  Quantum is the primary
                        engine (Iter 85); Classic-only markets keep
                        their Classic tag. */}
                    {m.engine === "quantum" && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1 py-px rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-[9px] font-black tracking-wider"
                        title="Powered by the Quantum ensemble engine (primary)"
                        data-testid={`schedule-market-engine-quantum-${fixture.match_id}-${i}`}
                      >
                        Q
                      </span>
                    )}
                    {m.engine === "classic" && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1 py-px rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[9px] font-black tracking-wider"
                        title="Powered by the Classic engine (Quantum doesn't publish this market)"
                        data-testid={`schedule-market-engine-classic-${fixture.match_id}-${i}`}
                      >
                        C
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    {oc === true && (
                      <span
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500/25 text-emerald-200 text-[9px] font-bold"
                        data-testid={`schedule-market-hit-${fixture.match_id}-${i}`}
                        title="Correct"
                      >
                        ✓
                      </span>
                    )}
                    {oc === false && (
                      <span
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-rose-500/25 text-rose-200 text-[9px] font-bold"
                        data-testid={`schedule-market-miss-${fixture.match_id}-${i}`}
                        title="Wrong"
                      >
                        ✗
                      </span>
                    )}
                    {/* Iter 145l — inline calibration tuner pill. */}
                    {m.calibration_pp != null && Math.abs(m.calibration_pp) >= 0.5 && (
                      <span
                        className={[
                          "text-[9px] font-bold font-tabular px-1 py-px rounded",
                          m.calibration_pp > 0
                            ? "bg-emerald-500/15 text-emerald-300"
                            : "bg-rose-500/15 text-rose-300",
                        ].join(" ")}
                        data-testid={`schedule-market-cal-${fixture.match_id}-${i}`}
                        title={`Auto-tuner adjusted ${m.calibration_pp > 0 ? "+" : ""}${m.calibration_pp}pp from raw ${m.raw_confidence}% (based on 30d miss-rate for this market+league)`}
                      >
                        {m.calibration_pp > 0 ? "↑" : "↓"}{Math.abs(m.calibration_pp)}
                      </span>
                    )}
                    <Badge variant="outline" className={["text-[10px] py-0 px-1.5", RISK_COLORS[m.risk_level] || ""].join(" ")}>
                      {Math.round(m.confidence)}%
                    </Badge>
                  </div>
                </div>
                <div className="text-sm font-semibold mt-1 truncate">{m.prediction}</div>
                {m.rationale && (
                  <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{m.rationale}</div>
                )}
                {/* Iter 85c — Compare-view flip.  Quantum is the
                    primary engine (Iter 85), so the side-by-side row
                    now shows the *other* engine's opinion for
                    transparency.  Uses inline classic_alt / quantum_alt
                    payloads served by /api/schedule/day — no extra
                    network hop needed. */}
                {m.engine === "quantum" && m.classic_alt && (
                  <div
                    className="mt-2 pt-2 border-t border-amber-500/20 flex items-center justify-between gap-2 text-[10px]"
                    data-testid={`schedule-market-classic-alt-${fixture.match_id}-${i}`}
                  >
                    <span className="inline-flex items-center gap-1 text-amber-300 font-black tracking-widest text-[9px]">
                      CLASSIC
                    </span>
                    <span className="text-foreground/80 truncate flex-1">{m.classic_alt.prediction}</span>
                    <span className="font-tabular text-foreground/80">
                      {m.classic_alt.confidence != null ? `${Math.round(m.classic_alt.confidence)}%` : "—"}
                    </span>
                  </div>
                )}
                {m.engine === "classic" && m.quantum_alt && (
                  <div
                    className="mt-2 pt-2 border-t border-cyan-500/20 flex items-center justify-between gap-2 text-[10px]"
                    data-testid={`schedule-market-quantum-alt-${fixture.match_id}-${i}`}
                  >
                    <span className="inline-flex items-center gap-1 text-cyan-300 font-black tracking-widest text-[9px]">
                      QUANTUM
                    </span>
                    <span className="text-foreground/80 truncate flex-1">{m.quantum_alt.prediction}</span>
                    <span className="font-tabular text-foreground/80">
                      {m.quantum_alt.confidence != null ? `${Math.round(m.quantum_alt.confidence)}%` : "—"}
                    </span>
                  </div>
                )}
                {/* Iter 145bh legacy — still surface the full Quantum
                    compare card (with HIT/MISS chip + calibration
                    detail) for classic-driven markets that don't
                    have an inline `quantum_alt` (e.g. Cards).  */}
                {m.engine === "classic" && !m.quantum_alt && (
                  <QuantumCompareBadge
                    q={quantumLookup.get(fixture.fixture_id, m.market_type)}
                    compact
                    testid={`schedule-market-quantum-${fixture.fixture_id}-${i}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SchedulePage;
